import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Flame, AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';

const formatApiError = (detail) => {
  if (detail == null) return "Si è verificato un errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { resetPassword } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Le password non corrispondono');
      return;
    }

    if (password.length < 6) {
      setError('La password deve essere di almeno 6 caratteri');
      return;
    }

    setLoading(true);

    try {
      await resetPassword(token, password);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505] p-8">
        <div className="bg-[#121212] border border-[#27272A] p-8 text-center">
          <AlertCircle className="w-12 h-12 text-[#E3000F] mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Token mancante</h2>
          <p className="text-[#A1A1AA] mb-4">Il link di reset non è valido.</p>
          <Link to="/forgot-password" className="text-[#E3000F] hover:underline">
            Richiedi un nuovo link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050505] p-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Flame className="w-16 h-16 text-[#E3000F] mx-auto mb-4" />
          <h1 className="text-3xl font-bold uppercase tracking-tighter" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            VVF Interventi
          </h1>
        </div>

        <div className="bg-[#121212] border border-[#27272A] p-8">
          <h2 className="text-2xl font-semibold uppercase tracking-tight mb-6" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            Nuova password
          </h2>

          {error && (
            <div className="bg-[#E3000F]/10 border border-[#E3000F]/30 p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-[#E3000F] flex-shrink-0 mt-0.5" />
              <p className="text-[#E3000F] text-sm">{error}</p>
            </div>
          )}

          {success ? (
            <div className="bg-[#009246]/10 border border-[#009246]/30 p-6 flex flex-col items-center gap-4">
              <CheckCircle className="w-12 h-12 text-[#009246]" />
              <div className="text-center">
                <p className="text-white font-medium mb-2">Password reimpostata!</p>
                <p className="text-[#A1A1AA] text-sm">
                  Verrai reindirizzato al login...
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="form-label">Nuova password</label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-[#0A0A0A] border-[#27272A] rounded-none h-12 text-white focus:border-[#E3000F] pr-12"
                    placeholder="••••••••"
                    required
                    data-testid="reset-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-white transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="form-label">Conferma password</label>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-[#0A0A0A] border-[#27272A] rounded-none h-12 text-white focus:border-[#E3000F]"
                  placeholder="••••••••"
                  required
                  data-testid="reset-confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#E3000F] hover:bg-[#CC000D] text-white font-semibold rounded-none h-12 uppercase tracking-wide border-2 border-transparent hover:border-[#E3000F] hover:bg-transparent transition-all"
                data-testid="reset-submit-btn"
              >
                {loading ? 'Salvataggio...' : 'Salva nuova password'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
